"use client";

import { useState, useEffect } from "react";
import {
  type CustomersOutput,
  type OrderOutput,
  OrderPaymentStatus,
  type OrdersQueryParams,
  OrderStatus,
  orderPaymentStatusTranslation,
  orderStatusTranslation,
} from "@/interfaces";
import { OrderService } from "@/services/OrderService";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CustomerService } from "@/services/CustomerService";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Download, Filter, RefreshCw } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { exportToExcel } from "@/util/exportUtils";
import { numberToMoney } from "@/util/moneyConversion";
import { DelinquencyTrendChart } from "./components/deliquency/DelinquencyTrendChart";
import { DelinquencyByCustomerChart } from "./components/deliquency/DelinquencyByCustomerChart";
import { DelinquencyByTimeChart } from "./components/deliquency/DelinquencyByTimeChart";
import { FilterDelinquency } from "./components/deliquency/FilterDelinquency";
import { DelinquencyTable } from "./components/deliquency/DelinquencyTable";

export function DelinquencyReport() {
  const [orders, setOrders] = useState<OrderOutput[]>([]);
  const [customers, setCustomers] = useState<CustomersOutput[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OrderOutput[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeView, setActiveView] = useState<"overview" | "details">(
    "overview"
  );

  const service = new OrderService();
  const customersService = new CustomerService();

  const fetchOrders = async (params?: OrdersQueryParams) => {
    try {
      const { data: fetchedOrders } = await service.findAll({
        ...params,
        paymentStatus: [
          OrderPaymentStatus.PARTIALLY_PAID,
          OrderPaymentStatus.PENDING,
        ],
        status: OrderStatus.DONE,
        expand: ["customers", "tags"],
      });
      setOrders(fetchedOrders);
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const { data: fetchedCustomers } = await customersService.findAll();
      setCustomers(fetchedCustomers);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchOrders(), fetchCustomers()]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await Promise.all([fetchOrders(), fetchCustomers()]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    setFilteredOrders(orders);
  }, [orders]);

  const handleFilterChange = (filters: OrdersQueryParams) => {
    fetchOrders(filters);
    setIsFilterOpen(false);
  };

  const delinquentOrders =
    filteredOrders?.filter(
      (order) => order.paymentStatus !== OrderPaymentStatus.PAID
    ) || [];

  const totalDelinquency = delinquentOrders.reduce((total, order) => {
    const paidAmount =
      order.payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
    return total + (order.totalAmount - paidAmount);
  }, 0);

  const delinquentCustomers = new Set(
    delinquentOrders
      .map((order) => {
        if (!order.customer) return null;
        return typeof order.customer === "string"
          ? order.customer
          : order.customer.id;
      })
      .filter(Boolean) as string[]
  );

  const delinquentCustomersCount = delinquentCustomers.size;

  const exportDelinquencyData = async () => {
    if (!delinquentOrders?.length) return;

    const data = delinquentOrders.map((order) => {
      const paidAmount =
        order.payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
      const pendingAmount = order.totalAmount - paidAmount;
      const customerName = order.customer
        ? typeof order.customer === "string"
          ? order.customer
          : order.customer.name || "Cliente sem nome"
        : "Cliente não identificado";

      return {
        "ID do Pedido": order.id || "",
        Cliente: customerName,
        Status: orderStatusTranslation[order.status] || order.status,
        "Status de Pagamento":
          orderPaymentStatusTranslation[order.paymentStatus] ||
          order.paymentStatus,
        "Valor Total (R$)": numberToMoney(order.totalAmount),
        "Valor Pago (R$)": numberToMoney(paidAmount),
        "Valor Pendente (R$)": numberToMoney(pendingAmount),
        "Data do Pedido": order.preparationDate
          ? new Date(order.preparationDate).toLocaleDateString()
          : "N/A",
        "Dias em Atraso": order.preparationDate
          ? Math.floor(
              (new Date().getTime() -
                new Date(order.preparationDate).getTime()) /
                (1000 * 3600 * 24)
            )
          : "N/A",
      };
    });

    await exportToExcel(data, "Relatório_de_Inadimplência");
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Calcular o valor total de pedidos
  const totalOrdersValue = delinquentOrders.reduce(
    (total, order) => total + order.totalAmount,
    0
  );

  // Calcular o percentual de inadimplência
  const delinquencyPercentage =
    totalOrdersValue > 0 ? (totalDelinquency / totalOrdersValue) * 100 : 0;

  return (
    <div className="p-4 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Dashboard de Inadimplência</h1>
          <p className="text-muted-foreground">
            Acompanhe e gerencie os pagamentos pendentes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={refreshData}
            disabled={refreshing}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
            />
            Atualizar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportDelinquencyData}
            disabled={!delinquentOrders?.length}
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Inadimplência
            </CardTitle>
            <Badge variant="destructive" className="ml-2">
              {delinquencyPercentage.toFixed(1)}%
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {numberToMoney(totalDelinquency || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              De um total de {numberToMoney(totalOrdersValue)} em pedidos
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Clientes Inadimplentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{delinquentCustomersCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {customers.length > 0
                ? `${(
                    (delinquentCustomersCount / customers.length) *
                    100
                  ).toFixed(1)}% do total de clientes`
                : "Carregando dados..."}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pedidos Inadimplentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{delinquentOrders?.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Média de {numberToMoney(
                totalDelinquency / (delinquentOrders?.length || 1)
              )}{" "}
              por pedido
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Tempo Médio de Atraso
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {delinquentOrders?.length &&
              delinquentOrders.some((order) => order.preparationDate)
                ? Math.floor(
                    delinquentOrders
                      .filter((order) => order.preparationDate)
                      .reduce((sum, order) => {
                        const days = Math.floor(
                          (new Date().getTime() -
                            new Date(order.preparationDate).getTime()) /
                            (1000 * 3600 * 24)
                        );
                        return sum + days;
                      }, 0) /
                      delinquentOrders.filter((order) => order.preparationDate)
                        .length
                  )
                : 0}{" "}
              dias
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Baseado na data de preparação dos pedidos
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col space-y-4">
        <div className="flex justify-center md:justify-start">
          <div className="inline-flex items-center rounded-md border border-input bg-background p-1 text-muted-foreground">
            <Button
              variant={activeView === "overview" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveView("overview")}
              className="rounded-sm px-3"
            >
              Visão Geral
            </Button>
            <Button
              variant={activeView === "details" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveView("details")}
              className="rounded-sm px-3"
            >
              Detalhes
            </Button>
          </div>
        </div>

        {activeView === "overview" ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Card className="col-span-1">
              <CardHeader>
                <CardTitle className="text-base">
                  Inadimplência por Cliente
                </CardTitle>
              </CardHeader>
              <CardContent className="h-80">
                <DelinquencyByCustomerChart orders={delinquentOrders} />
              </CardContent>
            </Card>

            <Card className="col-span-1">
              <CardHeader>
                <CardTitle className="text-base">
                  Tendência de Inadimplência
                </CardTitle>
              </CardHeader>
              <CardContent className="h-80">
                <DelinquencyTrendChart orders={delinquentOrders} />
              </CardContent>
            </Card>

            <Card className="col-span-1 md:col-span-2 lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-base">
                  Inadimplência por Tempo
                </CardTitle>
              </CardHeader>
              <CardContent className="h-80">
                <DelinquencyByTimeChart orders={delinquentOrders} />
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-6">
            <div className="md:hidden mb-4">
              <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline">
                    <Filter className="h-4 w-4 mr-2" />
                    Filtros
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="left"
                  className="w-full max-w-xs p-0 flex flex-col"
                >
                  <FilterDelinquency onFilterChange={handleFilterChange} />
                </SheetContent>
              </Sheet>
            </div>

            <div className="hidden md:block md:w-64 flex-shrink-0">
              <FilterDelinquency onFilterChange={handleFilterChange} />
            </div>

            <div className="flex-grow">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle>Pedidos Inadimplentes</CardTitle>
                  <Separator />
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <DelinquencyTable orders={delinquentOrders} />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
